import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Database,
  AlertTriangle,
  FileJson,
  FolderOpen,
  Image,
  Target,
  Layers,
  CheckCircle2,
  Box,
  Shapes,
  PersonStanding,
  Video,
  ScanText,
} from 'lucide-react';
import {
  mockTAO6InstanceDetails,
  TAO6Dataset,
  TAO6DatasetFormat,
  TAO6TaskType,
} from '@/mock/gpu-instance';
import { TAO6DatasetManager, TAO6DatasetCard } from '../../components/tao';

const TAODatasetsPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [selectedDataset, setSelectedDataset] = useState<TAO6Dataset | null>(null);
  const [datasetCardOpen, setDatasetCardOpen] = useState(false);
  const [datasetCardDataset, setDatasetCardDataset] = useState<TAO6Dataset | null>(null);

  const details = useMemo(() => mockTAO6InstanceDetails(instanceId || ''), [instanceId]);

  const handleViewDatasetCard = (dataset: TAO6Dataset) => {
    setDatasetCardDataset(dataset);
    setDatasetCardOpen(true);
  };

  const getFormatInfo = (format: TAO6DatasetFormat) => {
    switch (format) {
      case 'coco':
        return {
          name: 'COCO JSON',
          description: 'Common Objects in Context format with JSON annotations',
          icon: FileJson,
          color: 'text-blue-500',
          bgColor: 'bg-blue-500/10',
          structure: [
            'annotations/',
            '  instances_train.json',
            '  instances_val.json',
            'images/',
            '  train/',
            '  val/',
          ],
        };
      case 'kitti':
        return {
          name: 'KITTI',
          description: 'KITTI format with separate label files per image',
          icon: FolderOpen,
          color: 'text-purple-500',
          bgColor: 'bg-purple-500/10',
          structure: [
            'training/',
            '  image_2/',
            '  label_2/',
            'testing/',
            '  image_2/',
          ],
        };
      case 'directory':
        return {
          name: 'Directory',
          description: 'Class-based directory structure for classification',
          icon: Image,
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
          structure: [
            'train/',
            '  class_1/',
            '  class_2/',
            '  ...',
            'val/',
            '  class_1/',
            '  class_2/',
          ],
        };
    }
  };

  const getTaskInfo = (task: TAO6TaskType) => {
    switch (task) {
      case 'object_detection':
        return {
          name: 'Object Detection',
          icon: Target,
          color: 'text-green-500',
        };
      case 'image_classification':
        return {
          name: 'Image Classification',
          icon: Layers,
          color: 'text-blue-500',
        };
      case 'instance_segmentation':
        return {
          name: 'Instance Segmentation',
          icon: Box,
          color: 'text-purple-500',
        };
      case 'semantic_segmentation':
        return {
          name: 'Semantic Segmentation',
          icon: Shapes,
          color: 'text-cyan-500',
        };
      case 'pose_estimation':
        return {
          name: 'Pose Estimation',
          icon: PersonStanding,
          color: 'text-orange-500',
        };
      case 'action_recognition':
        return {
          name: 'Action Recognition',
          icon: Video,
          color: 'text-pink-500',
        };
      case 'ocr':
        return {
          name: 'OCR',
          icon: ScanText,
          color: 'text-amber-500',
        };
      default:
        return {
          name: task,
          icon: Database,
          color: 'text-gray-500',
        };
    }
  };

  // Statistics
  const detectionDatasets = details.datasets.filter(d => d.task === 'object_detection').length;
  const classificationDatasets = details.datasets.filter(d => d.task === 'image_classification').length;
  const totalImages = details.datasets.reduce((acc, d) => acc + d.trainCount + d.valCount + (d.testCount || 0), 0);

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6 text-purple-500" />
            TAO Datasets
          </h1>
          <p className="text-muted-foreground">
            Pre-loaded sample datasets for TAO 6.x training
          </p>
        </div>
      </div>

      {/* Dataset Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{details.datasets.length}</div>
            <div className="text-sm text-muted-foreground">Total Datasets</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-green-600">{detectionDatasets}</div>
            <div className="text-sm text-muted-foreground">Detection</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{classificationDatasets}</div>
            <div className="text-sm text-muted-foreground">Classification</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{totalImages.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground">Total Images</div>
          </CardContent>
        </Card>
      </div>

      {/* Format Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Supported Formats</CardTitle>
          <CardDescription>TAO 6.x supports multiple dataset formats</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {(['coco', 'kitti', 'directory'] as TAO6DatasetFormat[]).map((format) => {
              const info = getFormatInfo(format);
              const Icon = info.icon;
              return (
                <Card key={format} className="bg-muted/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${info.bgColor}`}>
                        <Icon className={`h-5 w-5 ${info.color}`} />
                      </div>
                      <div>
                        <div className="font-medium">{info.name}</div>
                        <div className="text-xs text-muted-foreground">{info.description}</div>
                      </div>
                    </div>
                    <div className="bg-background rounded p-2">
                      <pre className="text-xs font-mono text-muted-foreground whitespace-pre">
                        {info.structure.join('\n')}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Dataset Browser */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Available Datasets</CardTitle>
          <CardDescription>Select a dataset to use in your experiments</CardDescription>
        </CardHeader>
        <CardContent>
          <TAO6DatasetManager
            datasets={details.datasets}
            selectedDatasetId={selectedDataset?.id}
            onSelectDataset={setSelectedDataset}
            onViewDatasetCard={handleViewDatasetCard}
          />
        </CardContent>
      </Card>

      {/* Selected Dataset Details */}
      {selectedDataset && (
        <Card className="border-primary/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Selected Dataset: {selectedDataset.name}</CardTitle>
              <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Ready to Use
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Task & Format Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Task Type</div>
                <div className="flex items-center gap-2">
                  {getTaskInfo(selectedDataset.task).icon && (
                    <div className={getTaskInfo(selectedDataset.task).color}>
                      {(() => {
                        const Icon = getTaskInfo(selectedDataset.task).icon;
                        return <Icon className="h-4 w-4" />;
                      })()}
                    </div>
                  )}
                  <span className="font-medium">{getTaskInfo(selectedDataset.task).name}</span>
                </div>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Format</div>
                <div className="flex items-center gap-2">
                  {(() => {
                    const info = getFormatInfo(selectedDataset.format);
                    const Icon = info.icon;
                    return (
                      <>
                        <div className={info.color}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <span className="font-medium">{info.name}</span>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Split Counts */}
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 bg-muted/50 rounded-lg text-center">
                <div className="text-xs text-muted-foreground">Train</div>
                <div className="font-mono font-bold">{selectedDataset.trainCount.toLocaleString()}</div>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg text-center">
                <div className="text-xs text-muted-foreground">Validation</div>
                <div className="font-mono font-bold">{selectedDataset.valCount.toLocaleString()}</div>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg text-center">
                <div className="text-xs text-muted-foreground">Test</div>
                <div className="font-mono font-bold">{selectedDataset.testCount?.toLocaleString() || '-'}</div>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg text-center">
                <div className="text-xs text-muted-foreground">Classes</div>
                <div className="font-mono font-bold">{selectedDataset.classCount}</div>
              </div>
            </div>

            {/* All Classes */}
            <div>
              <div className="text-sm text-muted-foreground mb-2">Classes ({selectedDataset.classes.length}):</div>
              <div className="flex flex-wrap gap-1">
                {selectedDataset.classes.map((cls) => (
                  <Badge key={cls} variant="secondary" className="text-xs">
                    {cls}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Path */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">Path</div>
              <code className="text-sm font-mono">{selectedDataset.path}</code>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dataset Card Modal */}
      <TAO6DatasetCard
        dataset={datasetCardDataset}
        open={datasetCardOpen}
        onOpenChange={setDatasetCardOpen}
        onSelectDataset={(dataset) => {
          setSelectedDataset(dataset);
          setDatasetCardOpen(false);
        }}
      />

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> These are pre-loaded sample datasets for demonstration.
          In production, you can upload your own datasets in COCO, KITTI, or directory formats.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default TAODatasetsPage;
